import type {
  Course,
  CourseAttendanceStatus,
  CourseStatus
} from './types';

export interface ScheduleStats {
  total: number;
  past: number;
  current: number;
  future: number;
}

export function getCourseStatus(startTime: string, endTime: string, now: Date): CourseStatus {
  if (new Date(endTime) <= now) return 'past';
  if (new Date(startTime) <= now) return 'current';
  return 'future';
}

export function getCoursesForRoom(courses: Course[], roomId: string): Course[] {
  return courses
    .filter((course) => course.roomId === roomId)
    .sort((first, second) => first.startTime.localeCompare(second.startTime));
}

export function getCourseAttendanceStatus(course: Course, now: Date): CourseAttendanceStatus {
  if (course.attendanceCount > 0) return 'attended';
  const courseStatus = getCourseStatus(course.startTime, course.endTime, now);
  if (courseStatus === 'past') return 'overdue-unattended';
  if (courseStatus === 'current') return 'current-unattended';
  return 'pending-unattended';
}

export function hasOverdueUnattendedCourse(courses: Course[], now: Date): boolean {
  return courses.some((course) => getCourseAttendanceStatus(course, now) === 'overdue-unattended');
}

export function getFeaturedCourse(courses: Course[], now: Date): Course | undefined {
  const orderedCourses = [...courses].sort((first, second) => first.startTime.localeCompare(second.startTime));
  return orderedCourses.find((course) => getCourseStatus(course.startTime, course.endTime, now) === 'current')
    ?? orderedCourses.find((course) => getCourseStatus(course.startTime, course.endTime, now) === 'future');
}

export function getScheduleStats(courses: Course[], now: Date): ScheduleStats {
  return courses.reduce<ScheduleStats>(
    (stats, course) => {
      stats.total += 1;
      stats[getCourseStatus(course.startTime, course.endTime, now)] += 1;
      return stats;
    },
    { total: 0, past: 0, current: 0, future: 0 }
  );
}
